import { ZAPIER_API_V4_URL, ZAPIER_API_V4_URL_STAGING } from './constants';

/**
 * Zapier API Calls for Session, Account, Task Usage & Support Eligibility
 */

const baseUrl = () => {
  if (window.location.hostname === 'zapier-staging.com') {
    return ZAPIER_API_V4_URL_STAGING;
  }
  return ZAPIER_API_V4_URL;
};

export const fetchUser = async () => {
  try {
    const zapierApiUrl = baseUrl();
    const resp = await fetch(`${zapierApiUrl}/session/`, {
      credentials: 'include',
    });

    if (resp.status >= 400) {
      throw new Error('Error fetching user');
    }

    const data = await resp.json();
    // Sometimes, the server doesn't return a Date header, so we need to fallback to the client's current time
    // As of May 2023, this is happening on help.zapier.com, but not on zapier.com
    // More context in this working doc: https://zpr.io/z8JyGbt2ghje
    data.currentDateTime = resp.headers.get('Date')
      ? new Date(resp.headers.get('Date'))
      : new Date();
    return data;
  } catch (e) {
    console.error('Error fetching user', e);
  }
  return {};
};

export const fetchCurrentAccount = async user => {
  if (!user.is_logged_in) return null;

  try {
    const zapierApiUrl = baseUrl();
    const currentAccountId = user.current_account_id;
    if (!currentAccountId) {
      throw new Error('No account_id found.');
    }
    const resp = await fetch(`${zapierApiUrl}/accounts/${currentAccountId}`, {
      credentials: 'include',
    });

    if (resp.status >= 400) {
      throw new Error("Couldn't fetch user's account");
    }

    const data = await resp.json();
    return data;
  } catch (e) {
    console.error('Error fetching account', e);
  }
  return {};
};

/**
 * Fetches Support Eligibility data from the Zapier API
 * @returns Support Eligibility data for an user
 */
export const fetchSupportEligibility = async () => {
  try {
    const zapierApiUrl = baseUrl();
    const resp = await fetch(`${zapierApiUrl}/support-eligibility/`, {
      credentials: 'include',
    });

    if (resp.status >= 400) {
      throw new Error('Error fetching support eligibility');
    }

    const supportEligibilityData = await resp.json();
    return supportEligibilityData;
  } catch (e) {
    console.error('Error fetching support eligibility', e);
  }
  return {};
};

/**
 * Fetches the user and account data from the Zapier API
 * @param {object} initialData The initial data to passed from the entrypoint
 * @returns Eligibility data, an object composed of the initial data, the user and account data and support eligibility data
 */
export const fetchUserAndAccountData = async initialData => {
  const userData = (await fetchUser()) || {};
  const accountData = (await fetchCurrentAccount(userData)) || {};
  const eligibilityData = (await fetchSupportEligibility()) || {};
  const userAndAccountData = {
    ...initialData,
    userData,
    accountData,
    eligibilityData,
  };
  return userAndAccountData;
};

export const fetchTaskUsage = async currentAccountId => {
  try {
    const resp = await fetch(
      `${ZAPIER_API_V4_URL}/accounts/${currentAccountId}/task-usage`,
      {
        credentials: 'include',
      }
    );

    if (resp.status >= 400) {
      throw new Error('Error fetching task usage');
    }

    const data = await resp.json();
    return data;
  } catch (e) {
    console.error('Error fetching task usage', e);
  }
  return {};
};
